import React, { useState } from 'react'
import { Button } from 'components/ui/button'
import { Select } from 'components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from 'components/ui/dialog'
import { CalendarPlus } from 'lucide-react'
import { toast } from 'components/ui/toast-store'

export interface AddQuarterModalProps {
	isOpen: boolean
	onClose: () => void
	onAddQuarter: (quarter: number, year: number) => void
	existingQuarters?: { label: string; id: string }[]
}

export const AddQuarterModal: React.FC<AddQuarterModalProps> = ({
	isOpen,
	onClose,
	onAddQuarter,
	existingQuarters = [],
}) => {
	const currentYear = new Date().getFullYear()
	const [selectedQuarter, setSelectedQuarter] = useState<number>(1)
	const [selectedYear, setSelectedYear] = useState<number>(currentYear)

	const handleSubmit = () => {
		// Check if quarter already exists
		const quarterLabel = `Q${selectedQuarter} ${selectedYear}`
		const exists = existingQuarters.some(q => q.label === quarterLabel)

		if (exists) {
			toast.warning(`${quarterLabel} already exists. Please select a different quarter.`)
			return
		}

		onAddQuarter(selectedQuarter, selectedYear)
		onClose()

		// Reset to defaults
		setSelectedQuarter(1)
		setSelectedYear(currentYear)
	}

	const handleCancel = () => {
		onClose()
		// Reset to defaults
		setSelectedQuarter(1)
		setSelectedYear(currentYear)
	}

	// Generate year options (5 years back to 5 years forward)
	const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i)

	return (
		<Dialog open={isOpen} onClose={handleCancel}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
							<CalendarPlus className="w-5 h-5" />
						</div>
						<div>
							<DialogTitle>Add New Quarter</DialogTitle>
							<DialogDescription>Add a fiscal quarter to your income statement</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<div className="px-5 py-4 space-y-6">
					<p className="text-sm text-gray-600">
						Select a quarter and fiscal year to add to your income statement. The new quarter will appear
						as the leftmost column.
					</p>

					<div className="space-y-4">
						{/* Quarter Selection */}
						<div>
							<label htmlFor="quarter-select" className="block text-sm font-medium text-gray-700 mb-2">
								Quarter
							</label>
							<Select
								id="quarter-select"
								value={selectedQuarter}
								onChange={e => setSelectedQuarter(parseInt(e.target.value))}
							>
								<option value={1}>Q1 (January - March)</option>
								<option value={2}>Q2 (April - June)</option>
								<option value={3}>Q3 (July - September)</option>
								<option value={4}>Q4 (October - December)</option>
							</Select>
						</div>

						{/* Year Selection */}
						<div>
							<label htmlFor="year-select" className="block text-sm font-medium text-gray-700 mb-2">
								Fiscal Year
							</label>
							<Select
								id="year-select"
								value={selectedYear}
								onChange={e => setSelectedYear(parseInt(e.target.value))}
							>
								{yearOptions.map(year => (
									<option key={year} value={year}>
										{year}
									</option>
								))}
							</Select>
						</div>

						{/* Preview */}
						<div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
							<div className="flex items-center">
								<svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
									<path
										fillRule="evenodd"
										d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
										clipRule="evenodd"
									/>
								</svg>
								<div>
									<p className="text-sm font-medium text-blue-900">
										Adding: Q{selectedQuarter} {selectedYear}
									</p>
									<p className="text-xs text-blue-700 mt-1">
										This will be added as the leftmost (most recent) column
									</p>
								</div>
							</div>
						</div>

						{/* Existing Quarters Info */}
						{existingQuarters.length > 0 && (
							<div className="mt-4">
								<p className="text-xs text-gray-500 mb-2">Existing quarters:</p>
								<div className="flex flex-wrap gap-2">
									{existingQuarters.map(q => (
										<span
											key={q.id}
											className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
										>
											{q.label}
										</span>
									))}
								</div>
							</div>
						)}
					</div>
				</div>

				<DialogFooter>
					<Button variant="ghost" onClick={handleCancel}>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleSubmit}>
						Add Quarter
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
